import * as THREE from 'three';
import { bus } from '@/core/EventBus';
import type { Input } from '@/core/Input';
import type { Player } from '@/gameplay/Player';

export interface SafehouseEntrance {
  readonly id: string;
  readonly name: string;
  readonly position: THREE.Vector2;
  readonly radius: number;
}

export interface SafehouseSnapshot {
  readonly nearby: string | null;
  readonly inside: string | null;
}

export class SafehouseSystem {
  readonly #player: Player;
  readonly #entrances: readonly SafehouseEntrance[];
  #nearby: SafehouseEntrance | null = null;
  #inside: SafehouseEntrance | null = null;

  constructor(player: Player, entrances: readonly SafehouseEntrance[]) {
    this.#player = player;
    this.#entrances = entrances;
  }

  update(input: Input): void {
    this.#nearby = this.#nearestEntrance();
    if (this.#nearby && input.wasPressed('e')) {
      this.#inside = this.#nearby;
      bus.emit('safehouse:entered', { id: this.#inside.id, name: this.#inside.name });
    }
  }

  snapshot(): SafehouseSnapshot {
    return {
      nearby: this.#nearby?.id ?? null,
      inside: this.#inside?.id ?? null,
    };
  }

  #nearestEntrance(): SafehouseEntrance | null {
    let nearest: SafehouseEntrance | null = null;
    let nearestDistance = Infinity;
    for (const entrance of this.#entrances) {
      const distance = this.#player.position.distanceTo(entrance.position);
      if (distance > entrance.radius || distance >= nearestDistance) continue;
      nearest = entrance;
      nearestDistance = distance;
    }
    return nearest;
  }
}
